import {useEffect, useRef, useState} from 'react';
import {getKDMaxProduct} from 'service/KDMaxImportService';
import {get3dTemplate} from 'service/ProductService';
import {
    getDoorInfo,
    getPanelValidation,
    useQueryParams,
    getOptions,
    getCabinetFields,
} from 'shared/helpers';
import {useProductContext} from 'contexts/ProductContext';
import {getRequiredReferences} from 'components/customer/Product/helpers/getRequiredReferences';
import {getInitialValues} from 'components/customer/Product/helpers/getInitialValues';
import {mapInitialValues} from 'components/customer/Product/helpers/mapInitialValues';
import {getFormSchema} from 'components/customer/Product/helpers/getFormSchema';
import {useMaterialValues} from 'components/customer/Product/helpers/useMaterialValues';
import {cloneDeep} from 'lodash';
import {getMaterialRestrictionDetails} from 'components/customer/Product/helpers/getMaterialRestrictionDetails';
import {checkCabinetTopRestriction} from 'components/customer/Product/helpers/checkCabinetTopRestriction';
import {getFaceHeight} from 'components/customer/Product/Drawer/helpers';
import {useGetCabinetPreviewQuery} from 'components/customer/Preview3D/store/previewApi';
import {getSavedRangehoodHeight} from 'components/customer/Product/Appliance/helpers/getSavedRangehoodHeight';

export const useProductPreviewItem = ({slug, skip = false}) => {
    const {goto, height, width, depth, importid} = useQueryParams();
    const {
        setProduct,
        setInitialValuesRef,
        productDataStore,
        drawerRunnerInfoReference,
        simpleShelvesReference,
        productHasDoor,
        currentTab,
        setCurrentTab,
        formStructure,
        setFormStructure,
        validateField,
        defaultValuesReference,
        setMaterialOptions,
        setValidationData,
    } = useProductContext();
    const setMaterials = useMaterialValues();
    const shouldSkip = skip === true;

    const {data, isLoading, isError, error} = useGetCabinetPreviewQuery(
        {slug},
        {skip: shouldSkip}
    );

    const [loading, setLoading] = useState(false);
    const [initialValues, setInitialValues] = useState({});
    const [formSchema, setFormSchema] = useState({});
    const [isFavourite, setIsFavourite] = useState(false);
    const [tabErrors, setTabErrors] = useState({});
    const [productDetailCopy, setProductDetailCopy] = useState({});
    const [showRoomDefaultDialog, setShowRoomDefaultDialog] = useState(false);
    const [productInfo, setProductInfo] = useState();

    const validationRulesStore = useRef([]);
    const productValidationStore = useRef({});
    const shelvesStructureStore = useRef({});
    const shelfTypes = useRef({});
    const shelfStyles = useRef({});
    const shelfSectionHeight = useRef({});
    const partitionHeightReferenceStore = useRef();

    const initialLoad = async () => {
        const {
            job_cabinet: productValues,
            product_config: productConf,
            cabinet: currentProductDetail,
            product_info: productInformation,
        } = data;

        setProductInfo(productInformation);

        // Clone the product configuration to avoid mutation of the original data
        const ogProductConfig = {...productConf};
        const productConfig = cloneDeep(ogProductConfig);
        const productData = cloneDeep(productValues);
        const materialFields = getCabinetFields(productConfig.structure);
        const {validationData, materialOptions, materials} = setMaterials(
            productData,
            materialFields
        );

        const restrictions = getMaterialRestrictionDetails(
            materials,
            materialFields
        );
        const cabinetTopRestriction = checkCabinetTopRestriction(productData);

        if (typeof cabinetTopRestriction !== 'undefined') {
            restrictions.push(cabinetTopRestriction);
        }

        // Ensure productDetails is mutable
        let productDetails;
        if (productData.cabinet && productData.cabinet.attributes) {
            productDetails = cloneDeep(productData.cabinet.attributes);
        } else {
            productDetails = cloneDeep(currentProductDetail); // Ensure it's mutable
        }

        // Add productConfig to productDetails
        productDetails.productConfig = productConfig.structure;

        // Fetch KDMax product if importid exists
        const kdmaxProduct = importid ? await getKDMaxProduct(importid) : null;

        if (productData && !productData.template_3d?.length) {
            const template3d = await get3dTemplate(
                productData.type ?? productDetails?.id
            );

            if (template3d) {
                productData.template_3d = template3d;
            }
        }

        if (productData && productDetails) {
            setIsFavourite(parseInt(productData.favourites) > 0);

            productDataStore.current = productData;
            validationRulesStore.current = productConfig.validation;
            productHasDoor.current = getDoorInfo(productDetails.productConfig);

            let initialValues = getInitialValues(productConfig.structure);

            ({
                drawerRunnerInfoReference: drawerRunnerInfoReference.current,
                shelfSectionHeight: shelfSectionHeight.current,
                partitionHeightReferenceStore:
                    partitionHeightReferenceStore.current,
                shelvesStructureStore: shelvesStructureStore.current,
                shelfTypes: shelfTypes.current,
                shelfStyles: shelfStyles.current,
            } = getRequiredReferences(productConfig, initialValues));

            if (!productData.hasOwnProperty('cabinet')) {
                productDataStore.current.cabinet = {
                    attributes: productDetails,
                };
            }

            productValidationStore.current = getPanelValidation(
                productDataStore.current.cabinet.attributes
            );

            setProduct(productDetails);
            setProductDetailCopy(productDetails);
            setInitialValuesRef({...initialValues}); // Keep a copy of initial values

            const shelvesOptions = getOptions(
                'simple_shelves_amount',
                productConfig.structure
            );

            // this is a fallback value from existing code with existing conditions,
            //  as we are passing on slugs it is not necessary to use this but is an requirement
            const cabinetId = 0;

            initialValues = mapInitialValues({
                initialValues,
                productData,
                shelvesStructureStore,
                simpleShelvesReference,
                partitionHeightReferenceStore,
                cabinetId,
                shelvesOptions,
                drawerRunnerInfoReference: drawerRunnerInfoReference.current,
                productConfig: productDetails.productConfig,
            });

            const savedRangehoodHeight = getSavedRangehoodHeight(
                productData.job_cabinet_appliances
            );

            if (savedRangehoodHeight) {
                initialValues.rangehood_opening_height = savedRangehoodHeight;
            }

            if (width && initialValues.hasOwnProperty('cabinet_width')) {
                initialValues.cabinet_width = width;
            }

            if (goto === 'file-import') {
                initialValues.cabinet_height = height
                    ? height
                    : initialValues.cabinet_height;
                initialValues.cabinet_width = width
                    ? width
                    : initialValues.cabinet_width;
                initialValues.cabinet_depth = depth
                    ? depth
                    : initialValues.cabinet_depth;
                initialValues.cabinet_applied_panel_depth = depth
                    ? depth
                    : initialValues.cabinet_applied_panel_depth;

                if (kdmaxProduct?.shelves) {
                    initialValues.shelves = kdmaxProduct.shelves;
                    initialValues.cabinet_simple_shelves = false;
                    initialValues.simple_shelves_amount =
                        kdmaxProduct.shelfCount;
                    simpleShelvesReference.current = kdmaxProduct.shelfCount;
                }

                if (kdmaxProduct?.drawerDetails?.length) {
                    initialValues.cabinet_drawer_gap =
                        productData.cabinet_drawer_gap;
                    initialValues.cabinet_drawer_bottom =
                        kdmaxProduct.drawerDetails.bottomMargin;
                    initialValues.cabinet_drawer_top =
                        productData.cabinet_drawer_top;
                    initialValues.drawer_face_height =
                        kdmaxProduct.drawerDetails.faceDetails;
                }

                if (kdmaxProduct?.doorDetails) {
                    initialValues.cabinet_door_top =
                        kdmaxProduct.doorDetails.topMargin;
                    initialValues.cabinet_door_bottom =
                        kdmaxProduct.doorDetails.bottomMargin;
                }

                if (initialValues.hasOwnProperty('cabinet_panel_length')) {
                    initialValues.cabinet_panel_length = height;
                }

                if (initialValues.hasOwnProperty('cabinet_panel_width')) {
                    initialValues.cabinet_panel_width = width;
                }

                if (
                    productData?.cabinet?.attributes?.microwave ||
                    productData?.cabinet?.attributes?.oven
                ) {
                    let totalDrawerHeight =
                        initialValues.cabinet_total_drawer_height;

                    if (productData?.cabinet?.attributes?.microwave) {
                        totalDrawerHeight =
                            height -
                            initialValues.microwave_opening_height -
                            productData.ext_material.thickness * 2;
                    } else if (productData?.cabinet?.attributes?.oven) {
                        totalDrawerHeight =
                            height - initialValues.oven_opening_height;
                    }

                    initialValues.drawers = initialValues.drawers.map(
                        (drawer, i) => {
                            if (drawer.id === -1) {
                                const faceHeight = getFaceHeight(
                                    drawer,
                                    i,
                                    totalDrawerHeight,
                                    initialValues.cabinet_drawer_bottom,
                                    initialValues.cabinet_drawer_top,
                                    initialValues.cabinet_drawer_gap,
                                    'drawer_face_height',
                                    'drawer_style'
                                );
                                drawer.drawer_face_height = faceHeight;
                            }

                            return drawer;
                        }
                    );
                }

                initialValues.cabinet_note = kdmaxProduct.name;
            }

            setMaterialOptions(materialOptions);
            setValidationData({
                type: 'multiple',
                data: validationData,
            });

            defaultValuesReference.current = initialValues;
            validateField.current = true;

            if (materials) {
                if (materials.exterior && materialFields.hasExterior) {
                    initialValues.exterior_colour_name =
                        materials.exterior.name;
                }

                if (materials.carcase && materialFields.hasCarcase) {
                    initialValues.carcase_colour_name = materials.carcase.name;
                }
            }

            setInitialValues(initialValues);
            setFormSchema(
                getFormSchema(
                    {structure: productDetails.productConfig},
                    Object.keys(initialValues)
                )
            );
            setFormStructure(productConfig.structure); // Set form field structure
        }
    };

    useEffect(() => {
        if (productDetailCopy?.productConfig) {
            const newSchema = getFormSchema(
                {structure: productDetailCopy.productConfig},
                Object.keys(initialValues)
            );
            setFormSchema(newSchema);
        }
    }, [initialValues]);

    useEffect(() => {
        if (!isLoading && !isError && data) {
            setLoading(true);
            initialLoad();
        }

        if (isError) {
            throw new Error(
                error?.data?.error ?? 'Unable to get cabinet preview data'
            );
        }
    }, [data, isLoading, isError, error]);

    useEffect(() => {
        if (productInfo?.RoomProductNo && productInfo?.ProductDescription) {
            document.title = `${productInfo.RoomProductNo.replace('.00', '')} ${productInfo.ProductDescription}`;
        }
    }, [productInfo]);

    return {
        showRoomDefaultDialog,
        setShowRoomDefaultDialog,
        currentTab,
        setCurrentTab,
        initialValues,
        formSchema,
        formStructure,
        loading,
        setLoading,
        isFavourite,
        tabErrors,
        setTabErrors,
        productDataStore,
        productInfo,
    };
};
